import { Dictionary } from '@skylab-shared/types';
import { isEqual, isUndefined } from '@skylab-shared/utils';
import { v4 as uuid } from 'uuid';
import { INotifyPropertyChanged, INotifyPropertyChanging, OnPropertyChangedCallback, OnPropertyChangingCallback } from './../../observable';
import { ModelBuilder } from './model-builder';
import { ModelMetadataProvider } from './model-metadata-container';
import { PropertyMetadata } from './property-metadata';

export abstract class ModelBase implements INotifyPropertyChanged, INotifyPropertyChanging {
    private __values__: Dictionary<any>;

    private modelMetadata: ModelMetadataProvider;
    private propertyChangedHandlers: OnPropertyChangedCallback[];
    private propertyChangingHandlers: OnPropertyChangingCallback[];
    private internalModified: boolean;
    private internalModifiedProperties: Map<string, [unknown, unknown]>;

    protected initializing: boolean;

    public readonly version!: string;
    public readonly duplicatePreventionId: string;
    public readonly sun: string;

    /**
     * Sets only primitives.
     */
    public constructor();
    public constructor(data: any);
    public constructor(data?: any) {
        this.__values__ = {};

        this.internalModified = false;
        this.internalModifiedProperties = new Map();
        this.initializing = true;

        this.modelMetadata = Reflect.getMetadata('skl:model', this.constructor);
        this.propertyChangedHandlers = [];
        this.propertyChangingHandlers = [];

        ModelBuilder.build(this);
        ModelBuilder.populateWithDefaultData(this);
        this.sun = uuid();
        this.duplicatePreventionId = uuid();

        if (data) {
            this.sun = data.sun || this.sun;
            this.version = data.version;
            if (this.isPersistent) this.duplicatePreventionId = data.duplicatePreventionId;
            this.fill(data);
        }

        this.initializing = false;
    }

    public get isPersistent(): boolean {
        return this.version != null;
    }

    public get isModified(): boolean {
        return !this.initializing && this.internalModified;
    }

    public get modifiedProperties(): ReadonlyMap<string, [unknown, unknown]> {
        return this.internalModifiedProperties;
    }

    public clearModifiedProperties(): void {
        this.internalModifiedProperties = new Map();
    }

    public set(propertyName: string, value: any): boolean {
        let changed = false;
        const propertyMetadata = this.modelMetadata.getPropertyMetadata(propertyName);

        validateType(propertyMetadata, value);

        const old = this.__values__[propertyName];

        if (isEqual(value, old)) {
            return changed;
        }

        const cancel = !this.initializing && this.notifyPropertyChanging(propertyName, value, old);
        if (cancel) {
            return changed;
        }

        this.__values__[propertyName] = value;
        changed = true;

        if (!this.initializing) {
            this.internalModified = true;
            this.setInternalModifiedProperties(propertyName, old, value);
            this.notifyPropertyChanged(propertyName, old);
        }

        return changed;
    }

    private setInternalModifiedProperties(propertyName: string, oldValue: unknown, newValue: unknown): void {
        const prev = this.internalModifiedProperties.get(propertyName)?.[0];
        const result: [unknown, unknown] = [!isUndefined(prev) ? prev : oldValue, newValue];
        this.internalModifiedProperties.set(propertyName, result);
    }

    public get<T = any>(propertyName: string): T {
        return this.__values__[propertyName];
    }

    public onPropertyChanged(callback: OnPropertyChangedCallback): Function {
        this.propertyChangedHandlers.push(callback);
        return () => {
            this.propertyChangedHandlers = this.propertyChangedHandlers.filter(h => h !== callback);
        };
    }

    public onPropertyChanging(callback: OnPropertyChangingCallback): Function {
        this.propertyChangingHandlers.push(callback);
        return () => {
            this.propertyChangingHandlers = this.propertyChangingHandlers.filter(h => h !== callback);
        };
    }

    protected fill(data: any): void {
        this.modelMetadata.properties
            .filter(p => p.propertyName in data)
            .forEach(p => this.set(p.propertyName, data[p.propertyName]));
    }

    protected notifyPropertyChanged(propertyName: string, oldValue: any): void {
        const args = {
            sender: this,
            propertyName,
            oldValue
        };
        this.propertyChangedHandlers.forEach(h => h(args));
    }

    protected notifyPropertyChanging(propertyName: string, newValue: any, oldValue: any): boolean {
        const args = {
            sender: this,
            propertyName,
            newValue,
            oldValue,
            cancel: false
        };
        this.propertyChangingHandlers.forEach(h => h(args));
        return args.cancel;
    }
}

const validateType = (propertyMetadata: PropertyMetadata | null, value: any): void => {
    if (!propertyMetadata) return;
    if (value === void 0)
        throw new TypeError(`undefined is not assignable to type ${propertyMetadata.type?.name || 'unknown'} (property: ${propertyMetadata.propertyName}).`);
    if (value === null && (!propertyMetadata.config || !propertyMetadata.config.nullable) && propertyMetadata.type !== Object && propertyMetadata.type !== String)
        throw new TypeError(`null is not assignable to type ${propertyMetadata.type?.name || 'unknown'} (property: ${propertyMetadata.propertyName}).`);
    if (value !== null && propertyMetadata.type && value.constructor !== propertyMetadata.type)
        throw new TypeError(`${value.constructor.name} is not assignable to type ${propertyMetadata.type.name} (property: ${propertyMetadata.propertyName}).`);
};
